import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { X, Search, UserCheck } from 'lucide-react';
import { useDispatch, useSelector } from 'react-redux';

import { FormTemplate } from '../FormTemplate';
import { ButtonTemplate } from '../../../ui/buttons/ButtonTemplate';
import { IconButtonTemplate } from '../../../ui/buttons/IconButtonTemplate';
import { InputsTemplate } from '../../inputs/InputsTemplate';
import { LoaderProgress } from '../../../ui/LoaderProgress';

import { showSearch } from '../../../../redux/features/dashboard/dashboardSlice';
import {
  filterResidencesSearch,
  filterStreetsSearch,
} from '../../../../redux/features/dashboard/dashboardSlice';
import { selectSearchQuery } from '../../../../redux/features/dashboard/dashboardSelectors';
import {
  selectIsAdmin,
  selectUserToken,
} from '../../../../redux/features/user/userSelectors';
import { useGetSearchFiltersDataQuery } from '../../../../redux/services/search/searchApi';
import { useGetCitiesForCountryQuery } from '../../../../redux/services/data/citiesApi';
import { useGetAllAgentsQuery } from '../../../../redux/services/agents/agentsApi';
import { useGetStreetsForCityQuery } from '../../../../redux/services/data/streetsApi';
import { useGetResidencesForCityQuery } from '../../../../redux/services/data/residencesApi';

import {
  initialAdditionalValues,
  initialSearchValuesNames,
  localisationSelectData,
  mockAdditional,
  getMockMore,
  searchValuesNames,
} from '../../../../lib/mocks/search-mock';
import { generateSearchQueriesRealty } from '../../../../utils/utils';
import { colors, EMPTY_ARRAY } from '../../../../data/constants';

export const FiltersMapForm = ({
  handleCancel = () => {},
  isResetClicked = false,
  setIsResetClicked = () => {},
}) => {
  const dispatch = useDispatch();
  const userToken = useSelector(selectUserToken);
  const isAdmin = useSelector(selectIsAdmin);
  const searchQuery = useSelector(selectSearchQuery);

  const [multiSelectRefs, setMultiSelectRefs] = useState({});
  const [datePickerRefs, setDatePickerRefs] = useState({});
  const [statesInputsFromTo, setStatesInputsFromTo] = useState([]);
  const [formRef, setFormRef] = useState(null);
  const [isMineOnly, setIsMineOnly] = useState(false);

  const [selectedCityId, setSelectedCityId] = useState(null);
  const citiesIdRef = useRef(new Set());
  const [removedCityId, setRemovedCityId] = useState(null);

  const { data: filters = {}, isLoading: isFiltersLoading } =
    useGetSearchFiltersDataQuery(userToken);
  const { data: cities, isLoading: isCitiesLoading } =
    useGetCitiesForCountryQuery({ country_id: 73 });
  const { data: agentOptions = EMPTY_ARRAY, isFetching: isAgentsLoading } =
    useGetAllAgentsQuery(userToken, { skip: !isAdmin });

  useGetStreetsForCityQuery(
    { token: userToken, city_id: selectedCityId, isCityArray: true },
    { skip: !selectedCityId || !userToken, refetchOnMountOrArgChange: true }
  );
  useGetResidencesForCityQuery(
    { token: userToken, city_id: selectedCityId, isCityArray: true },
    { skip: !selectedCityId || !userToken, refetchOnMountOrArgChange: true }
  );

  const isLoading = isFiltersLoading || isCitiesLoading || isAgentsLoading;

  const cityOptions = useMemo(
    () =>
      cities?.map(c => ({ ...c, value: c.id, title: c.name || c.title })) ?? [],
    [cities]
  );

  const filterOptions = useMemo(() => {
    if (!filters) return {};
    return Object.fromEntries(
      Object.entries(filters).map(([key, items]) => [
        key,
        items.map(item => ({ ...item, value: item.id })),
      ])
    );
  }, [filters]);

  const handleCityIdRemove = useCallback((_valuesList, _name, removedItem) => {
    if (removedItem) setRemovedCityId(removedItem.value);
  }, []);

  const inputs = useMemo(() => {
    const enrich = field => {
      if (field.name === searchValuesNames.city) {
        return {
          ...field,
          selectData: cityOptions,
          handleSelection: handleCityIdRemove,
        };
      }
      if (field.name === searchValuesNames.user) {
        return { ...field, selectData: agentOptions };
      }
      if (filterOptions[field.name]) {
        return { ...field, selectData: filterOptions[field.name] };
      }
      return field;
    };
    return [...mockAdditional.map(enrich), ...getMockMore(isAdmin).map(enrich)];
  }, [cityOptions, agentOptions, filterOptions, handleCityIdRemove, isAdmin]);

  const handleSelectsData = values => {
    setSelectedCityId(null);
    const cityValues = values;

    if (
      cityValues?.length !== citiesIdRef.current.size &&
      removedCityId != null
    ) {
      dispatch(filterStreetsSearch(removedCityId));
      dispatch(filterResidencesSearch(removedCityId));
      setRemovedCityId(null);
    }

    if (Array.isArray(cityValues) && cityValues.length > 0) {
      cityValues.forEach(city => {
        if (!citiesIdRef.current.has(city)) {
          citiesIdRef.current.add(city);
          setSelectedCityId(city);
        }
      });
    } else {
      dispatch(filterStreetsSearch([]));
      dispatch(filterResidencesSearch([]));
    }
  };

  useEffect(() => {
    if (removedCityId != null) {
      citiesIdRef.current.delete(removedCityId);
    }
  }, [removedCityId]);

  const handleSubmit = async values => {
    const query = await generateSearchQueriesRealty(
      initialSearchValuesNames,
      localisationSelectData,
      searchValuesNames,
      { ...values, [searchValuesNames.editable]: isMineOnly ? 1 : '' }
    );
    dispatch(
      showSearch({
        isSearchVisible: true,
        searchQuery: searchQuery ?? '',
        searchFilters: query,
      })
    );
    handleCancel();
  };

  const handleResetClick = () => {
    Object.entries(multiSelectRefs).forEach(([refName, ref]) => {
      if (ref?.current?.resetSelectedValues) {
        ref.current.resetSelectedValues();
        ref.current.state.searchValue = '';
        ref.setFieldValue(refName, '');
      }
    });
    Object.entries(datePickerRefs).forEach(([refName, ref]) => {
      if (ref?.current?.clearDate) {
        ref.current.clearDate();
        ref.setFieldValue(refName, '');
      }
    });
    statesInputsFromTo.forEach(setState => setState(''));
    setIsMineOnly(false);
    citiesIdRef.current.clear();
    dispatch(filterStreetsSearch([]));
    dispatch(filterResidencesSearch([]));
  };

  useEffect(() => {
    if (isResetClicked) handleResetClick();
  }, [isResetClicked]);

  return (
    <div className="flex flex-col w-full h-full overflow-hidden bg-whiteColor rounded exclude-click dialog-with-click">
      <div className="flex justify-between items-center px-4 pt-3 pb-2 border-b shrink-0">
        <h6 className="font-bold text-blackColor">Filters</h6>
        <IconButtonTemplate
          bgColor="border-whiteColor bg-whiteColor"
          handleClick={handleCancel}
          icon={X}
          size={20}
        />
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto px-4 py-3">
        {isLoading && <LoaderProgress />}

        {!isLoading && (
          <FormTemplate
            initialValues={initialAdditionalValues}
            handleSubmitForm={handleSubmit}
            isWithoutButton={true}
            setFormRef={setFormRef}
          >
            {formProps => (
              <>
                <CityWatcher
                  cityValue={formProps?.values?.[searchValuesNames.city]}
                  onChange={handleSelectsData}
                />
                <div className="flex flex-col gap-3 w-full">
                  <InputsTemplate
                    formProps={formProps}
                    inputsList={inputs}
                    setMultiSelectRefs={setMultiSelectRefs}
                    setDatePickerRefs={setDatePickerRefs}
                    setStatesInputsFromTo={setStatesInputsFromTo}
                    isResetClicked={isResetClicked}
                    setIsResetClicked={setIsResetClicked}
                  />
                </div>
                {/* Enables native Enter-submit from any text input on desktop
                    and virtual-keyboard "Go" on mobile */}
                <button
                  type="submit"
                  aria-hidden="true"
                  tabIndex={-1}
                  className="hidden"
                />
              </>
            )}
          </FormTemplate>
        )}
      </div>

      <div
        className="flex justify-end gap-2 px-4 py-3 border-t shrink-0"
        style={{ paddingBottom: 'calc(0.75rem + env(safe-area-inset-bottom))' }}
      >
        <ButtonTemplate
          text="Mine + pool"
          icon={UserCheck}
          isIconText={true}
          color={isMineOnly ? undefined : colors.blackColor}
          bgColor={isMineOnly ? 'bg-greenColor border-greenColor' : false}
          classes={isMineOnly ? '' : 'bg-white'}
          tooltipText="Only realties you can edit: yours and the import pool"
          handleClick={() => setIsMineOnly(prev => !prev)}
        />
        <ButtonTemplate
          text="Clear form"
          handleClick={handleResetClick}
        />
        <ButtonTemplate
          text="Apply"
          bgColor="bg-redColor border-redColor"
          icon={Search}
          isIconText={true}
          handleClick={() => {
            // Blur any focused input so InputFromTo's dropdown-close chain
            // commits fresh values into Formik before submit reads them.
            document.activeElement?.blur?.();
            formRef?.current?.submitForm?.();
          }}
        />
      </div>
    </div>
  );
};

const CityWatcher = ({ cityValue, onChange }) => {
  useEffect(() => {
    onChange(cityValue);
  }, [cityValue]);
  return null;
};
